// File: src/pages/SettingsPage.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import MinimalFooter from '../components/MinimalFooter';

// Icons
import {
  IoPersonOutline,
  IoLockClosedOutline,
  IoNotificationsOutline,
  IoDocumentTextOutline,
  IoHelpCircleOutline,
  IoLogOutOutline,
  IoTrashOutline,
  IoChevronForward,
  IoChevronDown,
  IoPeopleOutline,
} from 'react-icons/io5';

import { LuPencil, LuTickets } from 'react-icons/lu';

// Toaster + Logout sheet
import { useToaster } from '../context/ToasterContext';
import LogoutBottomSheet from '../components/LogoutBottomSheet';

export default function SettingsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToaster();

  const userId = localStorage.getItem('userId') || '123';

  // Basic user data
  const [userObject, setUserObject] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [profilePicUrl, setProfilePicUrl] = useState('');

  const [genderStr, setGenderStr] = useState('');
  const [ageStr, setAgeStr] = useState('');

  // Referral Partner access
  const [isReferralPartner, setIsReferralPartner] = useState(false);

  // Communication toggles
  const [textCommEnabled, setTextCommEnabled] = useState(false);
  const [textCommSaving, setTextCommSaving] = useState(false);

  // Accordion state
  const [openItem, setOpenItem] = useState(null);

  // Logout bottom sheet
  const [showLogoutSheet, setShowLogoutSheet] = useState(false);

  // Open the logout sheet when SimpleRow dispatches the custom event
  useEffect(() => {
    const openSheet = () => setShowLogoutSheet(true);

    window.addEventListener(
      'open-logout-sheet',
      openSheet
    );

    return () =>
      window.removeEventListener(
        'open-logout-sheet',
        openSheet
      );
  }, []);

  // ============================================================
  // LOAD USER
  // ============================================================

  useEffect(() => {
    axios
      .get(
        `${process.env.REACT_APP_API_URL}/users/${userId}`
      )
      .then((res) => {
        const user = res.data || {};

        setUserObject(user);

        setFirstName(
          user.firstName || ''
        );

        setLastName(
          user.lastName || ''
        );

        setUserEmail(
          user.email || ''
        );

        setPhone(
          user.phoneNumber || ''
        );

        setTextCommEnabled(
          user.smsOptIn === true
        );

        setProfilePicUrl(
          user.profilePicUrl || ''
        );

        if (user.gender) {
          setGenderStr(
            user.gender
          );
        }

        if (user.dateOfBirth) {
          const now = new Date();
          const dob = new Date(
            user.dateOfBirth
          );

          let age =
            now.getFullYear() -
            dob.getFullYear();

          const m =
            now.getMonth() -
            dob.getMonth();

          if (
            m < 0 ||
            (
              m === 0 &&
              now.getDate() <
                dob.getDate()
            )
          ) {
            age--;
          }

          setAgeStr(
            String(age)
          );
        }
      })
      .catch((err) => {
        console.error(
          'Failed to fetch user =>',
          err
        );
      });
  }, [userId]);

  // ============================================================
  // CHECK REFERRAL PARTNER ACCESS
  // ============================================================

  useEffect(() => {
    let active = true;

    async function checkReferralPartner() {
      try {
        const token =
          localStorage.getItem(
            'accessToken'
          );

        if (!token) {
          if (active) {
            setIsReferralPartner(false);
          }

          return;
        }

        await axios.get(
          `${process.env.REACT_APP_API_URL}/referral-partners/me`,
          {
            headers: {
              Authorization:
                `Bearer ${token}`,
            },
          }
        );

        if (active) {
          setIsReferralPartner(true);
        }
      } catch (err) {
        if (!active) {
          return;
        }

        setIsReferralPartner(false);

        // 401 = not logged in
        // 403 = logged in but not an active referral partner
        if (
          err?.response?.status !== 401 &&
          err?.response?.status !== 403
        ) {
          console.error(
            'Failed to check referral partner status =>',
            err
          );
        }
      }
    }

    checkReferralPartner();

    return () => {
      active = false;
    };
  }, []);

  // Navigate to subpage for updates
  const handleUpdate = (mode) => {
    navigate(
      `/account-update/${mode}?userId=${userId}`
    );
  };

  const handleTextCommToggle = async () => {
    if (textCommSaving) {
      return;
    }

    const token =
      localStorage.getItem(
        'accessToken'
      );

    if (!token) {
      addToast(
        'Please log in again to update text preferences.',
        'error',
        4000
      );

      return;
    }

    const nextValue =
      !textCommEnabled;

    setTextCommSaving(true);

    try {
      const res =
        await axios.patch(
          `${process.env.REACT_APP_API_URL}/users/${userId}/sms-preference`,
          {
            enabled: nextValue,
          },
          {
            headers: {
              Authorization:
                `Bearer ${token}`,
            },
          }
        );

      const enabled =
        res.data?.smsOptIn ===
        true;

      setTextCommEnabled(
        enabled
      );

      setUserObject(
        (current) =>
          current
            ? {
                ...current,
                smsOptIn:
                  enabled,
                smsOptInAt:
                  res.data
                    ?.smsOptInAt ??
                  current.smsOptInAt,
                smsOptInPhoneNumber:
                  res.data
                    ?.smsOptInPhoneNumber ??
                  current.smsOptInPhoneNumber,
                smsConsentVersion:
                  res.data
                    ?.smsConsentVersion ??
                  current.smsConsentVersion,
                smsOptOutAt:
                  res.data
                    ?.smsOptOutAt ??
                  current.smsOptOutAt,
                smsOptOutSource:
                  res.data
                    ?.smsOptOutSource ??
                  current.smsOptOutSource,
              }
            : current
      );

      addToast(
        enabled
          ? 'Text updates enabled.'
          : 'Text updates disabled.',
        'success',
        3000
      );
    } catch (err) {
      console.error(
        'Failed to update text preferences =>',
        err
      );

      addToast(
        err?.response?.data
          ?.message ||
          'Could not update text preferences.',
        'error',
        4000
      );
    } finally {
      setTextCommSaving(
        false
      );
    }
  };

  // Toasts from prior screens
  useEffect(() => {
    if (
      location.state
        ?.feedbackSubmitted
    ) {
      addToast(
        'Thank you! Your feedback was submitted.',
        'success',
        4000
      );

      location.state.feedbackSubmitted =
        false;
    }

    if (
      location.state?.updatedType
    ) {
      switch (
        location.state.updatedType
      ) {
        case 'email':
          addToast(
            'Email updated successfully!',
            'success',
            4000
          );
          break;

        case 'phone':
          addToast(
            'Phone number updated successfully!',
            'success',
            4000
          );
          break;

        case 'password':
          addToast(
            'Password updated successfully!',
            'success',
            4000
          );
          break;

        default:
          break;
      }

      location.state.updatedType =
        null;
    }
  }, [
    location.state,
    addToast,
  ]);

  // -----------------------------
  // MOBILE RENDER
  // -----------------------------

  function renderSettingsMobile() {
    return (
      <div
        style={
          mobileStyles.pageWrapper
        }
      >
        <div
          style={
            mobileStyles.accordionWrapper
          }
        >

          {/* 0) Membership */}
          <SimpleRow
            icon={
              <LuTickets
                style={
                  mobileStyles.outlineIcon
                }
              />
            }
            headline="Reservation Settings"
            subLabel="Manage your card on file"
            onClick={() =>
              navigate(
                '/membership'
              )
            }
          />

          {/* Referral Partner */}
          {isReferralPartner && (
            <SimpleRow
              icon={
                <IoPeopleOutline
                  style={
                    mobileStyles.outlineIcon
                  }
                />
              }
              headline="Referral Dashboard"
              subLabel="View referrals, earnings, and payments"
              onClick={() =>
                navigate(
                  '/partner'
                )
              }
            />
          )}

          {/* 1) Contact */}
          <AccordionItem
            itemKey="contact"
            icon={
              <IoPersonOutline
                style={
                  mobileStyles.outlineIcon
                }
              />
            }
            headline="Contact"
            subLabel="Update your email and phone"
            isOpen={
              openItem ===
              'contact'
            }
            onToggle={() =>
              setOpenItem(
                openItem ===
                  'contact'
                  ? null
                  : 'contact'
              )
            }
            content={
              <div
                style={
                  mobileStyles.expandedContent
                }
              >
                <div
                  style={
                    mobileStyles.formGroup
                  }
                >
                  <label
                    style={
                      mobileStyles.inputLabel
                    }
                  >
                    Email
                  </label>

                  <div
                    style={{
                      position:
                        'relative',
                    }}
                  >
                    <input
                      type="email"
                      readOnly
                      style={
                        mobileStyles.readOnlyInput
                      }
                      value={
                        userEmail
                      }
                    />

                    <LuPencil
                      style={
                        mobileStyles.pencilIcon
                      }
                      onClick={() =>
                        handleUpdate(
                          'email'
                        )
                      }
                    />
                  </div>
                </div>

                <div
                  style={
                    mobileStyles.formGroup
                  }
                >
                  <label
                    style={
                      mobileStyles.inputLabel
                    }
                  >
                    Phone
                  </label>

                  <div
                    style={{
                      position:
                        'relative',
                    }}
                  >
                    <input
                      type="text"
                      readOnly
                      style={
                        mobileStyles.readOnlyInput
                      }
                      value={
                        phone
                      }
                    />

                    <LuPencil
                      style={
                        mobileStyles.pencilIcon
                      }
                      onClick={() =>
                        handleUpdate(
                          'phone'
                        )
                      }
                    />
                  </div>
                </div>
              </div>
            }
          />

          {/* 2) Password */}
          <AccordionItem
            itemKey="password"
            icon={
              <IoLockClosedOutline
                style={
                  mobileStyles.outlineIcon
                }
              />
            }
            headline="Password"
            subLabel="Change your password"
            isOpen={
              openItem ===
              'password'
            }
            onToggle={() =>
              setOpenItem(
                openItem ===
                  'password'
                  ? null
                  : 'password'
              )
            }
            content={
              <div
                style={
                  mobileStyles.expandedContent
                }
              >
                <label
                  style={
                    mobileStyles.inputLabel
                  }
                >
                  Password
                </label>

                <div
                  style={{
                    position:
                      'relative',
                  }}
                >
                  <input
                    type="password"
                    readOnly
                    style={
                      mobileStyles.readOnlyInput
                    }
                    value="********"
                  />

                  <LuPencil
                    style={
                      mobileStyles.pencilIcon
                    }
                    onClick={() =>
                      handleUpdate(
                        'password'
                      )
                    }
                  />
                </div>
              </div>
            }
          />

          {/* 3) Communications */}
          <AccordionItem
            itemKey="communications"
            icon={
              <IoNotificationsOutline
                style={
                  mobileStyles.outlineIcon
                }
              />
            }
            headline="Communications"
            subLabel="Text message preferences"
            isOpen={
              openItem ===
              'communications'
            }
            onToggle={() =>
              setOpenItem(
                openItem ===
                  'communications'
                  ? null
                  : 'communications'
              )
            }
            content={
              <div
                style={
                  mobileStyles.expandedContent
                }
              >
                <div
                  style={{
                    ...mobileStyles.commCard,

                    ...(textCommEnabled
                      ? mobileStyles.commCardActive
                      : {}),

                    ...(textCommSaving
                      ? mobileStyles.commCardSaving
                      : {}),
                  }}
                  onClick={
                    handleTextCommToggle
                  }
                  aria-disabled={
                    textCommSaving
                  }
                >
                  <div
                    style={
                      mobileStyles.commLabel
                    }
                  >
                    Text Alerts
                  </div>

                  <div
                    style={
                      textCommEnabled
                        ? mobileStyles.switchOn
                        : mobileStyles.switchOff
                    }
                  >
                    <div
                      style={
                        textCommEnabled
                          ? mobileStyles.thumbOn
                          : mobileStyles.thumbOff
                      }
                    />
                  </div>
                </div>

                <div
                  style={
                    mobileStyles.smsDisclosure
                  }
                >
                  By enabling text
                  updates, you agree to
                  receive recurring SMS
                  messages from Tribe.
                  Message frequency
                  varies. Message and
                  data rates may apply.
                  Reply STOP to
                  unsubscribe.{' '}

                  <button
                    type="button"
                    style={
                      mobileStyles.smsDisclosureLink
                    }
                    onClick={() =>
                      navigate(
                        '/terms'
                      )
                    }
                  >
                    Terms
                  </button>

                  {' · '}

                  <button
                    type="button"
                    style={
                      mobileStyles.smsDisclosureLink
                    }
                    onClick={() =>
                      navigate(
                        '/privacy'
                      )
                    }
                  >
                    Privacy
                  </button>
                </div>
              </div>
            }
          />

          {/* 4) Policies */}
          <AccordionItem
            itemKey="policies"
            icon={
              <IoDocumentTextOutline
                style={
                  mobileStyles.outlineIcon
                }
              />
            }
            headline="Policies"
            subLabel="Privacy, terms, and guidelines"
            isOpen={
              openItem ===
              'policies'
            }
            onToggle={() =>
              setOpenItem(
                openItem ===
                  'policies'
                  ? null
                  : 'policies'
              )
            }
            content={
              <div
                style={
                  mobileStyles.expandedContent
                }
              >
                <div
                  style={
                    mobileStyles.policyRow
                  }
                >
                  <div>
                    Privacy Policy
                  </div>

                  <button
                    style={
                      mobileStyles.linkButton
                    }
                    onClick={() =>
                      navigate(
                        '/legal/privacy'
                      )
                    }
                  >
                    View
                  </button>
                </div>

                <div
                  style={
                    mobileStyles.policyRow
                  }
                >
                  <div>
                    Terms of Service
                  </div>

                  <button
                    style={
                      mobileStyles.linkButton
                    }
                    onClick={() =>
                      navigate(
                        '/legal/terms'
                      )
                    }
                  >
                    View
                  </button>
                </div>

                <div
                  style={
                    mobileStyles.policyRow
                  }
                >
                  <div>
                    Community Guidelines
                  </div>

                  <button
                    style={
                      mobileStyles.linkButton
                    }
                    onClick={() =>
                      navigate(
                        '/legal/community'
                      )
                    }
                  >
                    View
                  </button>
                </div>
              </div>
            }
          />

          {/* 5) Support */}
          <SimpleRow
            icon={
              <IoHelpCircleOutline
                style={
                  mobileStyles.outlineIcon
                }
              />
            }
            headline="Support"
            subLabel="Get help or send feedback"
            onClick={() =>
              navigate(
                '/support-feedback'
              )
            }
          />

          {/* 6) Log Out */}
          <SimpleRow
            icon={
              <IoLogOutOutline
                style={
                  mobileStyles.outlineIcon
                }
              />
            }
            headline="Log Out"
            subLabel="Sign out of your account"
            onClick={(e) => {
              e.preventDefault();

              window.dispatchEvent(
                new Event(
                  'open-logout-sheet'
                )
              );
            }}
          />

          {/* 7) Delete Account */}
          <SimpleRow
            icon={
              <IoTrashOutline
                style={
                  mobileStyles.outlineIcon
                }
              />
            }
            headline="Delete Account"
            subLabel="Permanently delete your account"
            onClick={() =>
              navigate(
                `/delete-account/${userId}`
              )
            }
          />
        </div>

        {/* LOGOUT BOTTOM SHEET */}
        <LogoutBottomSheet
          visible={
            showLogoutSheet
          }
          onCancel={() =>
            setShowLogoutSheet(
              false
            )
          }
          onConfirm={() => {
            localStorage.removeItem(
              'accessToken'
            );

            localStorage.removeItem(
              'refreshToken'
            );

            localStorage.removeItem(
              'userId'
            );

            localStorage.removeItem(
              'sessionId'
            );

            window.location.href =
              '/login';
          }}
        />
      </div>
    );
  }

  // -----------------------------
  // ACCORDION ITEM
  // -----------------------------

  function AccordionItem({
    itemKey,
    icon,
    headline,
    subLabel,
    isOpen,
    onToggle,
    content,
  }) {
    const hasContent =
      !!content;

    return (
      <div
        style={
          mobileStyles.itemContainer
        }
      >
        <div
          style={
            mobileStyles.itemHeader
          }
          onClick={
            onToggle
          }
        >
          <div
            style={
              mobileStyles.iconArea
            }
          >
            <div
              style={
                mobileStyles.iconCircle
              }
            >
              {icon}
            </div>
          </div>

          <div
            style={
              mobileStyles.labelArea
            }
          >
            <div
              style={
                mobileStyles.headline
              }
            >
              {headline}
            </div>

            {subLabel && (
              <div
                style={
                  mobileStyles.subLabel
                }
              >
                {subLabel}
              </div>
            )}
          </div>

          {hasContent && (
            isOpen ? (
              <IoChevronDown
                style={
                  mobileStyles.arrowIcon
                }
              />
            ) : (
              <IoChevronForward
                style={
                  mobileStyles.arrowIcon
                }
              />
            )
          )}
        </div>

        {isOpen &&
          hasContent && (
            <div
              style={
                mobileStyles.expandedPanel
              }
            >
              {content}
            </div>
          )}

        <div
          style={
            mobileStyles.divider
          }
        />
      </div>
    );
  }

  // -----------------------------
  // SIMPLE ROW
  // -----------------------------

  function SimpleRow({
    icon,
    headline,
    subLabel,
    onClick,
    highlighted = false,
  }) {
    return (
      <div
        style={{
          ...mobileStyles.itemContainer,

          ...(highlighted
            ? mobileStyles.highlightedItem
            : {}),
        }}
      >
        <div
          style={
            mobileStyles.itemHeader
          }
          onClick={
            onClick
          }
        >
          <div
            style={
              mobileStyles.iconArea
            }
          >
            <div
              style={
                mobileStyles.iconCircle
              }
            >
              {icon}
            </div>
          </div>

          <div
            style={
              mobileStyles.labelArea
            }
          >
            <div
              style={
                mobileStyles.headline
              }
            >
              {headline}
            </div>

            {subLabel && (
              <div
                style={
                  mobileStyles.subLabel
                }
              >
                {subLabel}
              </div>
            )}
          </div>
        </div>

        <div
          style={
            mobileStyles.divider
          }
        />
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection:
          'column',
        minHeight: '85vh',
        background: '#fff',
      }}
    >
      <div
        style={{
          flex: 1,
        }}
      >
        {renderSettingsMobile()}
      </div>

      <MinimalFooter />
    </div>
  );
}

/*************************************************************
 * MOBILE STYLES — “Uber-like” aesthetic (monochrome, hairlines)
 *************************************************************/

const LEFT_INDENT = 44;

const mobileStyles = {
  pageWrapper: {
    width: '100%',
    maxWidth: 760,
    margin:
      '12px auto 24px',

    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",

    color: '#0b0b0b',
  },

  accordionWrapper: {
    display: 'flex',
    flexDirection:
      'column',
    width: '100%',
    boxSizing:
      'border-box',
    padding:
      '0 16px',
  },

  /* Rows */

  itemContainer: {
    width: '100%',
    boxSizing:
      'border-box',
  },

  itemHeader: {
    display: 'flex',
    alignItems:
      'center',
    cursor: 'pointer',
    padding:
      '12px 0',
  },

  /* Icons */

  iconArea: {
    width:
      `${LEFT_INDENT}px`,

    minWidth:
      `${LEFT_INDENT}px`,

    display: 'flex',

    justifyContent:
      'flex-start',

    alignItems:
      'center',
  },

  iconCircle: {
    width: 28,
    height: 28,

    borderRadius: 8,

    display: 'grid',

    placeItems:
      'center',

    background:
      '#f8f8f8ff',

    border:
      '0px solid #e5e7eb',

    color:
      '#0b0b0b',
  },

  outlineIcon: {
    fontSize: 16,
  },

  /* Labels */

  labelArea: {
    flex: 1,

    display: 'flex',

    flexDirection:
      'column',

    boxSizing:
      'border-box',

    minWidth: 0,
  },

  headline: {
    fontSize: 14,

    fontWeight: 800,

    marginBottom: 2,

    color:
      '#0b0b0b',

    lineHeight: 1.2,
  },

  subLabel: {
    fontSize: 12,

    color:
      '#6b7280',

    lineHeight: 1.3,
  },

  arrowIcon: {
    fontSize: 18,

    color:
      '#6b7280',

    marginLeft: 8,

    flexShrink: 0,
  },

  /* Expansion area */

  expandedPanel: {
    marginLeft:
      `${LEFT_INDENT}px`,

    paddingTop: 8,

    paddingBottom: 8,

    boxSizing:
      'border-box',
  },

  divider: {
    height: 1,

    backgroundColor:
      '#eef0f2',

    marginLeft:
      `${LEFT_INDENT}px`,
  },

  /* Content blocks */

  expandedContent: {
    marginTop: 6,

    maxWidth:
      'calc(100% - 70px)',
  },

  formGroup: {
    marginBottom: 12,
  },

  inputLabel: {
    display: 'block',

    marginBottom: 6,

    fontSize: 12,

    fontWeight: 700,

    color:
      '#374151',

    letterSpacing: 0.2,
  },

  readOnlyInput: {
    width: '100%',

    boxSizing:
      'border-box',

    padding:
      '10px 36px 10px 12px',

    fontSize: 14,

    border:
      '1px solid #e5e7eb',

    borderRadius: 10,

    backgroundColor:
      '#f9fafb',

    color:
      '#111827',

    cursor:
      'not-allowed',

    outline: 'none',
  },

  pencilIcon: {
    position:
      'absolute',

    right: 10,

    top: '50%',

    transform:
      'translateY(-50%)',

    fontSize: 16,

    color:
      '#6b7280',

    cursor:
      'pointer',
  },

  /* Communication cards */

  commCard: {
    display: 'flex',

    alignItems:
      'center',

    justifyContent:
      'space-between',

    border:
      '1px solid #e5e7eb',

    borderRadius: 12,

    padding:
      '10px 12px',

    marginBottom: 10,

    cursor:
      'pointer',

    background:
      '#fff',
  },

  commCardActive: {
    borderColor:
      '#d1d5db',

    boxShadow:
      '0 1px 4px rgba(0,0,0,0.06)',
  },

  commCardSaving: {
    opacity: 0.6,

    cursor:
      'wait',
  },

  commLabel: {
    fontSize: 14,

    color:
      '#111827',

    fontWeight: 600,
  },

  smsDisclosure: {
    marginTop: -2,

    marginBottom: 10,

    padding:
      '0 2px',

    fontSize: 10.5,

    lineHeight: 1.45,

    color:
      '#6b7280',
  },

  smsDisclosureLink: {
    padding: 0,

    border: 'none',

    background:
      'transparent',

    color:
      '#4b5563',

    font: 'inherit',

    fontWeight: 700,

    textDecoration:
      'underline',

    cursor:
      'pointer',
  },

  /* iOS-like switches */

  switchOn: {
    width: 44,

    height: 26,

    borderRadius: 20,

    background:
      '#0b0b0b',

    border:
      '1px solid #0b0b0b',

    position:
      'relative',
  },

  switchOff: {
    width: 44,

    height: 26,

    borderRadius: 20,

    background:
      '#f3f4f6',

    border:
      '1px solid #e5e7eb',

    position:
      'relative',
  },

  thumbOn: {
    position:
      'absolute',

    top: '50%',

    right: 2,

    width: 22,

    height: 22,

    transform:
      'translateY(-50%)',

    boxSizing:
      'border-box',

    borderRadius:
      '50%',

    background:
      '#fff',
  },

  thumbOff: {
    position:
      'absolute',

    top: '50%',

    left: 2,

    width: 22,

    height: 22,

    transform:
      'translateY(-50%)',

    boxSizing:
      'border-box',

    borderRadius:
      '50%',

    background:
      '#fff',

    border:
      '1px solid #e5e7eb',
  },

  /* Policy rows */

  policyRow: {
    display: 'flex',

    alignItems:
      'center',

    justifyContent:
      'space-between',

    background:
      '#f9fafb',

    padding:
      '10px 12px',

    borderRadius: 12,

    marginBottom: 10,

    color:
      '#111827',

    fontSize: 14,

    border:
      '1px solid #e5e7eb',
  },

  linkButton: {
    backgroundColor:
      '#0b0b0b',

    color:
      '#fff',

    borderRadius: 999,

    border: 'none',

    padding:
      '8px 14px',

    cursor:
      'pointer',

    fontSize: 12,

    fontWeight: 700,
  },
};